import type { DefaultWalletOptions } from "@rainbow-me/rainbowkit/dist/wallets/Wallet";
import { metaMaskWallet } from "@rainbow-me/rainbowkit/wallets";
import { createStore } from "mipd";
import type { EIP1193Provider } from "viem";
import { createConnector } from "wagmi";
import { injected } from "wagmi/connectors";

let mipdStore: ReturnType<typeof createStore> | null = null;

function getMipdStore() {
  if (typeof window === "undefined") return null;
  if (mipdStore === null) {
    mipdStore = createStore();
  }
  return mipdStore;
}

/** Same exclusions as @rainbow-me/rainbowkit `metaMaskWallet` (never treat OKX/other mimics as MetaMask). */
function isRainbowKitMetaMask(ethereum: unknown): boolean {
  const e = ethereum as Record<string, unknown> | null | undefined;
  if (!e?.isMetaMask) return false;
  if (e.isBraveWallet && !e._events && !e._state) return false;
  if (e.isApexWallet) return false;
  if (e.isAvalanche) return false;
  if (e.isBackpack) return false;
  if (e.isBifrost) return false;
  if (e.isBitKeep) return false;
  if (e.isBitski) return false;
  if (e.isBlockWallet) return false;
  if (e.isCoinbaseWallet) return false;
  if (e.isDawn) return false;
  if (e.isEnkrypt) return false;
  if (e.isExodus) return false;
  if (e.isFrame) return false;
  if (e.isFrontier) return false;
  if (e.isGamestop) return false;
  if (e.isHyperPay) return false;
  if (e.isImToken) return false;
  if (e.isKuCoinWallet) return false;
  if (e.isMathWallet) return false;
  if (e.isNestWallet) return false;
  if (e.isOkxWallet || e.isOKExWallet) return false;
  if (e.isOneInchIOSWallet || e.isOneInchAndroidWallet) return false;
  if (e.isOpera) return false;
  if (e.isPhantom) return false;
  if (e.isPortal) return false;
  if (e.isRabby) return false;
  if (e.isRainbow) return false;
  if (e.isStatus) return false;
  if (e.isTalisman) return false;
  if (e.isTally) return false;
  if (e.isTokenPocket) return false;
  if (e.isTokenary) return false;
  if (e.isTrust || e.isTrustWallet) return false;
  if (e.isXDEFI) return false;
  if (e.isZeal) return false;
  if (e.isZerion) return false;
  if (e.__seif) return false;
  return true;
}

/**
 * Wagmi's `injected` passes a minimal window type (not `lib.dom` `Window`) to `target.provider`.
 * Use the same structural shape so we don't clash with the global DOM interface.
 */
type InjectedProviderWindow = {
  ethereum?: unknown;
};

function findMetaMaskProvider(win: InjectedProviderWindow | undefined): EIP1193Provider | undefined {
  if (!win) return undefined;

  const from6963 = getMipdStore()?.findProvider({ rdns: "io.metamask" })?.provider;
  if (from6963 && isRainbowKitMetaMask(from6963)) {
    return from6963 as EIP1193Provider;
  }

  const eth = win.ethereum as (typeof win.ethereum & { providers?: unknown[] }) | undefined;
  if (!eth) return undefined;

  const providers = eth.providers;
  if (providers?.length) {
    const found = providers.find((p) => isRainbowKitMetaMask(p));
    if (found) return found as EIP1193Provider;
  }

  return isRainbowKitMetaMask(eth) ? (eth as EIP1193Provider) : undefined;
}

/**
 * RainbowKit's MetaMask connector uses `providers.find(isMetaMask) ?? window.ethereum`, so when OKX
 * owns `window.ethereum` the fallback connects to OKX. Wagmi's `injected({ target: 'metaMask' })`
 * avoids that fallback but can still race with EIP-6963. We resolve `io.metamask` via mipd first,
 * then scan `ethereum.providers` / a single provider with the same predicate RainbowKit uses.
 */
export function metaMaskWalletFixed(options: DefaultWalletOptions) {
  const base = metaMaskWallet(options);
  if (base.installed === undefined) {
    return base;
  }
  return {
    ...base,
    createConnector: (walletDetails) =>
      createConnector((config) => ({
        ...injected({
          shimDisconnect: true,
          target: {
            id: "metaMask",
            name: "MetaMask",
            provider: (win: InjectedProviderWindow | undefined) => findMetaMaskProvider(win),
          },
        })(config),
        rdns: "io.metamask",
        ...walletDetails,
      })),
  };
}
